(() => {
  'use strict';

  const STUDIO_SELECTOR = '[data-lds-hub-sill-studio]';
  const FLOW_COLOR = 'Flow RGB';
  const MAX_TEXT_LENGTH = 24;
  const TEXT_MAX_WIDTH = 420;
  const COLOR_ALIASES = {
    'navy blue': 'Blue',
  };
  const FORM_FIELD_NAMES = Object.freeze({
    material: 'contact[Material]',
    productType: 'contact[Product type]',
  });

  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  const studioBrand = root => root.dataset.studioBrand?.trim() || 'Vehicle';
  const defaultStudioText = root => studioBrand(root).toUpperCase();

  const selectedColor = root => root.querySelector('[data-sill-color]:checked');

  const selectedMaterial = root => {
    const select = root.querySelector('[data-sill-material-select]');
    const option = select?.selectedOptions?.[0];
    return {
      select,
      option,
      key: select?.value || 'stainless',
      label: option?.dataset.materialLabel || option?.textContent?.trim() || 'Stainless Steel',
      image: option?.dataset.image || '',
    };
  };

  const selectedVehicle = root => {
    const hub = root.closest('[data-lds-accessories-hub]') || document;
    const selection = hub.querySelector('[data-hub-selected]');
    const label = !selection || !selection.hidden
      ? selection?.querySelector('[data-hub-selected-label]')?.textContent?.trim()
      : '';
    return label || studioBrand(root);
  };

  const selectedGeneration = root => {
    const hub = root.closest('[data-lds-accessories-hub]') || document;
    const selection = hub.querySelector('[data-hub-selected]');
    if (selection?.hidden) return '';
    const select = hub.querySelector('[data-vehicle-generation]');
    const option = select?.selectedOptions?.[0];
    if (!option || !option.value) return '';
    return option.textContent.trim();
  };

  const setRootState = (root, text, color, material) => {
    const vehicle = selectedVehicle(root);
    const generation = selectedGeneration(root);
    const values = {
      'data-studio-text': text,
      'data-studio-color': color,
      'data-studio-material': material.label,
      'data-studio-material-key': material.key,
      'data-studio-vehicle': vehicle,
      'data-studio-generation': generation,
    };
    Object.entries(values).forEach(([name, value]) => root.setAttribute(name, value || ''));
    return {vehicle, generation};
  };

  const flowGradient = root => root.querySelector('[data-sill-flow-gradient]');

  const setFlowAnimation = (state, enabled) => {
    state.flowActive = enabled;
    if (!enabled || reducedMotion() || typeof window.requestAnimationFrame !== 'function') {
      if (state.frame !== null) window.cancelAnimationFrame?.(state.frame);
      state.frame = null;
      state.previousTime = null;
      if (state.gradient) state.gradient.setAttribute('gradientTransform', 'translate(0 0)');
      return;
    }
    if (state.frame !== null) return;
    const tick = time => {
      if (!state.flowActive || reducedMotion() || !state.root.isConnected) {
        state.frame = null;
        state.previousTime = null;
        return;
      }
      if (state.previousTime !== null) state.phase = (state.phase + Math.min(time - state.previousTime, 100) * 0.08) % 480;
      state.previousTime = time;
      state.gradient?.setAttribute('gradientTransform', `translate(${state.phase} 0)`);
      state.frame = window.requestAnimationFrame(tick);
    };
    state.frame = window.requestAnimationFrame(tick);
  };

  const measuredTextWidth = text => {
    try {
      const box = text.getBBox();
      const computed = typeof text.getComputedTextLength === 'function' ? text.getComputedTextLength() : 0;
      return Math.max(Number(box.width) || 0, Number(computed) || 0);
    } catch (_error) {
      return Math.max(text.textContent.length * 24, 1);
    }
  };

  const fitText = text => {
    text.removeAttribute('textLength');
    text.removeAttribute('lengthAdjust');
    text.style.removeProperty('font-size');
    const measured = measuredTextWidth(text);
    if (measured <= TEXT_MAX_WIDTH) return;

    const computedSize = Number.parseFloat(window.getComputedStyle(text).fontSize) || 42;
    const nextSize = Math.max(16, computedSize * (TEXT_MAX_WIDTH / measured));
    text.style.fontSize = `${nextSize}px`;
    if (measuredTextWidth(text) > TEXT_MAX_WIDTH) {
      text.setAttribute('textLength', String(TEXT_MAX_WIDTH));
      text.setAttribute('lengthAdjust', 'spacingAndGlyphs');
    }
  };

  const setMaterialImage = (root, material) => {
    const image = root.querySelector('[data-sill-material-image]');
    if (!image || !material.image) return;
    if (image.getAttribute('src') !== material.image) image.setAttribute('src', material.image);
    image.alt = `${material.label} door sill design preview`;
  };

  const draw = (state, announce = false) => {
    const {root, input, text, lines, colorLabel, status} = state;
    const value = input.value.trim().slice(0, MAX_TEXT_LENGTH) || defaultStudioText(root);
    const colorInput = selectedColor(root);
    const color = colorInput?.value || 'White';
    const paint = color === FLOW_COLOR
      ? `url(#${state.gradient?.id || ''})`
      : colorInput?.dataset.color || '#ffffff';
    const material = selectedMaterial(root);
    input.value = input.value.slice(0, MAX_TEXT_LENGTH);
    text.textContent = value;
    text.setAttribute('x', '720');
    text.setAttribute('y', '250');
    text.setAttribute('fill', paint);
    lines.forEach(line => line.setAttribute('stroke', paint));
    fitText(text);
    colorLabel.textContent = color;
    setMaterialImage(root, material);
    const context = setRootState(root, value, color, material);
    state.flowActive = color === FLOW_COLOR;
    setFlowAnimation(state, state.flowActive);
    state.svg?.setAttribute('aria-label', `${material.label} door sill with ${value} in ${color}${color === FLOW_COLOR ? ' animated' : ''} LED preview`);
    if (announce) status.textContent = `${material.label} preview, ${color} LED.`;
    return {...context, value, color, material};
  };

  const setSelectColor = (field, value) => {
    if (!field) return;
    const wanted = value.trim();
    const option = [...field.options].find(item => item.value.trim().toLowerCase() === wanted.toLowerCase())
      || [...field.options].find(item => item.textContent.trim().toLowerCase() === wanted.toLowerCase())
      || [...field.options].find(item => item.value.trim().toLowerCase() === (COLOR_ALIASES[wanted.toLowerCase()] || '').toLowerCase());
    if (option) {
      field.value = option.value;
      return;
    }
    const added = new Option(wanted, wanted, false, true);
    field.add(added);
    field.value = wanted;
  };

  const hiddenField = (form, fieldName, value) => {
    let field = form.querySelector(`input[name="${fieldName}"]`);
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = fieldName;
      form.appendChild(field);
    }
    field.value = value;
  };

  const fillModal = root => {
    const modal = document.querySelector('[data-lds-custom-design-modal]');
    const form = modal?.querySelector('form');
    if (!modal || modal.hidden || !form) return false;

    const input = root.querySelector('[data-sill-text-input]');
    const colorInput = selectedColor(root);
    const material = selectedMaterial(root);
    const text = input?.value.trim().slice(0, MAX_TEXT_LENGTH) || defaultStudioText(root);
    const color = colorInput?.value || 'White';
    const vehicle = selectedVehicle(root);
    const generation = selectedGeneration(root);
    const vehicleField = form.querySelector('[name="contact[Vehicle brand and model]"]');
    const colorField = form.querySelector('[name="contact[LED color]"]');
    const detailsField = form.querySelector('[name="contact[Logo or text details]"]');
    const notesField = form.querySelector('[name="contact[Additional notes]"]');
    if (vehicleField) vehicleField.value = vehicle;
    setSelectColor(colorField, color);
    if (detailsField) detailsField.value = `Text preview: ${text}`;
    if (notesField) {
      notesField.value = [
        'Product type: Door Sills',
        `Material: ${material.label}`,
        `Vehicle: ${vehicle}`,
        `Selected generation: ${generation || 'Not selected'}`,
        `LED color: ${color}`,
        `Preview text: ${text}`,
        'Design preview. Final artwork and fit confirmed before production.',
      ].join('\n');
    }
    hiddenField(form, FORM_FIELD_NAMES.material, material.label);
    hiddenField(form, FORM_FIELD_NAMES.productType, 'Door Sills');
    root.setAttribute('data-studio-form-context', 'ready');
    return true;
  };

  const scheduleModalContext = root => {
    let attempts = 0;
    const apply = () => {
      attempts += 1;
      if (fillModal(root) || attempts >= 20) return;
      window.setTimeout(apply, 40);
    };
    queueMicrotask(apply);
  };

  const init = root => {
    if (!root || root.dataset.ldsHubSillStudioReady === 'v1') return;
    const input = root.querySelector('[data-sill-text-input]');
    const text = root.querySelector('[data-sill-preview-text]');
    const svg = root.querySelector('[data-sill-preview-svg]');
    const gradient = flowGradient(root);
    const lines = [...root.querySelectorAll('[data-sill-led-lines] line')];
    const colorLabel = root.querySelector('[data-sill-color-label]');
    const status = root.querySelector('[data-sill-status]');
    const materialSelect = root.querySelector('[data-sill-material-select]');
    const trigger = root.querySelector('[data-lds-custom-design-trigger]');
    if (!input || !text || !svg || !gradient || lines.length !== 2 || !colorLabel || !status || !materialSelect || !trigger) return;

    root.dataset.ldsHubSillStudioReady = 'v1';
    const state = {root, input, text, svg, gradient, lines, colorLabel, status, frame: null, previousTime: null, phase: 0, flowActive: false};
    const render = announce => draw(state, announce);
    input.addEventListener('input', () => render(false));
    root.querySelectorAll('[data-sill-color]').forEach(radio => radio.addEventListener('change', () => render(true)));
    materialSelect.addEventListener('change', () => render(true));
    trigger.addEventListener('click', () => {
      render(false);
      trigger.dataset.ldsCustomDesignSource = root.dataset.studioSource || 'hub_sill_studio';
      scheduleModalContext(root);
    });

    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    motionQuery?.addEventListener?.('change', () => setFlowAnimation(state, state.flowActive));
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => fitText(text)).observe(svg);
    window.addEventListener('resize', () => fitText(text), {passive: true});
    document.fonts?.ready.then(() => fitText(text));
    render(false);
  };

  const initAll = () => document.querySelectorAll(STUDIO_SELECTOR).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, {once: true});
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();

// Keep the existing badge studio usable inside the pilot's compact preview.
(() => {
  const init = () => document.querySelectorAll('[data-lds-accessories-hub] [data-lds-badge-text-preview]').forEach(root => {
    const text = root.querySelector('[data-badge-preview-text]');
    const stage = root.querySelector('[data-badge-preview-stage]');
    const input = root.querySelector('[data-badge-preview-input]');
    if (!text || !stage || !input) return;
    const fitCompactPreview = () => {
      if (root.dataset.badgeTooLong !== 'true') return;
      text.setAttribute('font-size', '80');
      text.setAttribute('x', '603'); text.setAttribute('y', '645');
      const box = text.getBBox();
      const fit = Math.min(1, 980 / Math.max(text.getComputedTextLength(), box.width, 1), 78 / Math.max(box.height, 1));
      // Keep genuinely excessive input subject to the original validation.
      if (80 * fit < 24) return;
      text.setAttribute('font-size', String(80 * fit));
      const fitted = text.getBBox();
      text.setAttribute('x', String(1206 - (fitted.x + fitted.width / 2)));
      text.setAttribute('y', String(1290 - (fitted.y + fitted.height / 2)));
      text.style.visibility = 'visible';
      root.dataset.badgeTooLong = 'false';
      input.setAttribute('aria-invalid', 'false');
      root.querySelector('[data-badge-preview-error]').hidden = true;
      const color = root.querySelector('[data-badge-preview-color]:checked')?.value || 'White';
      root.querySelector('[data-badge-preview-svg]').setAttribute('aria-label', `License Plate Badge with ${text.textContent} in ${color}. Preview.`);
    };
    const schedule = () => requestAnimationFrame(fitCompactPreview);
    root.addEventListener('input', schedule);
    root.addEventListener('change', schedule);
    new ResizeObserver(schedule).observe(stage);
    document.fonts?.ready.then(schedule);
    schedule();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
